import { frontendConfig } from '@kiku/config/env/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PageContainer } from '~/components/layouts/page-container';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Separator } from '~/components/ui/separator';
import { useDocumentTitle } from '~/hooks/use-document-title';
import { useSlugIds } from '~/hooks/use-slug-ids';
import { api } from '~/lib/api';
import { resolveOrgId } from '~/lib/slug-resolver';
import { orgSettingsQueryOptions } from '~/queries/org-settings.queries';

const TIMEZONES = Intl.supportedValuesOf('timeZone');

export const Route = createFileRoute('/_authenticated/org/$orgSlug/settings/')({
  loader: ({ context, params }) => {
    const orgId = resolveOrgId(context.queryClient, params.orgSlug);
    void context.queryClient.prefetchQuery(orgSettingsQueryOptions(orgId));
  },
  component: OrgSettingsPage
});

function OrgSettingsPage(): React.ReactNode {
  const { t } = useTranslation();
  useDocumentTitle(t('orgSettings.title'));
  const { orgSlug } = Route.useParams();
  const { orgId } = useSlugIds(orgSlug);
  const queryClient = useQueryClient();

  const { data: settings } = useQuery({ ...orgSettingsQueryOptions(orgId), enabled: !!orgId });

  function invalidate(): void {
    void queryClient.invalidateQueries(orgSettingsQueryOptions(orgId));
  }

  return (
    <PageContainer breadcrumbs={[{ label: t('orgSettings.title') }]} title={t('orgSettings.title')}>
      <div className="flex flex-col gap-6">
        <BrandingSection orgId={orgId} logoUrl={settings?.logoUrl ?? null} onChanged={invalidate} />
        <Separator />
        <TimezoneSection
          orgId={orgId}
          currentTimezone={settings?.timezone ?? 'UTC'}
          onSaved={invalidate}
        />
      </div>
    </PageContainer>
  );
}

function BrandingSection({
  orgId,
  logoUrl,
  onChanged
}: {
  orgId: string;
  logoUrl: string | null;
  onChanged: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const base = frontendConfig.VITE_API_BASE_URL.replace(/\/$/, '');
      const res = await fetch(`${base}/api/org/${orgId}/settings/logo`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      if (!res.ok) {
        const json = (await res.json()) as { error?: string };
        throw new Error(json.error ?? t('orgSettings.branding.uploadError'));
      }
    },
    onSuccess: () => {
      toast.success(t('orgSettings.branding.uploadSuccess'));
      onChanged();
    },
    onError: (err: Error) => {
      toast.error(err.message);
    }
  });

  const removeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].settings.logo.$delete({ param: { orgId } });
      if (!res.ok) throw new Error(t('errors.unexpected'));
    },
    onSuccess: () => {
      toast.success(t('orgSettings.branding.removeSuccess'));
      onChanged();
    },
    onError: () => {
      toast.error(t('errors.unexpected'));
    }
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate(file);
    e.target.value = '';
  }

  const isPending = uploadMutation.isPending || removeMutation.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold">{t('orgSettings.branding.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('orgSettings.branding.logoDescription')}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={t('orgSettings.branding.logo')}
              className="size-full object-contain p-1"
            />
          ) : (
            <ImageIcon className="size-8 text-muted-foreground" />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/svg+xml"
            className="sr-only"
            onChange={handleFileChange}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={isPending}
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              {uploadMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {t('orgSettings.branding.uploadLogo')}
            </Button>
            {logoUrl && (
              <Button
                variant="ghost"
                size="sm"
                disabled={isPending}
                onClick={() => {
                  removeMutation.mutate();
                }}
              >
                {removeMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )}
                {t('orgSettings.branding.removeLogo')}
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t('orgSettings.branding.logoHint')}</p>
        </div>
      </div>
    </div>
  );
}

function TimezoneSection({
  orgId,
  currentTimezone,
  onSaved
}: {
  orgId: string;
  currentTimezone: string;
  onSaved: () => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const [timezone, setTimezone] = useState(currentTimezone);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.api.org[':orgId'].settings.$patch({
        param: { orgId },
        json: { timezone }
      });
      if (!res.ok) throw new Error(t('orgSettings.timezone.saveError'));
    },
    onSuccess: () => {
      toast.success(t('orgSettings.timezone.saveSuccess'));
      onSaved();
    },
    onError: () => {
      toast.error(t('orgSettings.timezone.saveError'));
    }
  });

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold">{t('orgSettings.timezone.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('orgSettings.timezone.description')}
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-2 sm:w-72">
          <Label htmlFor="timezone-select">{t('orgSettings.timezone.label')}</Label>
          <Select
            value={timezone}
            onValueChange={(v) => {
              if (v !== null) setTimezone(v);
            }}
          >
            <SelectTrigger id="timezone-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          onClick={() => {
            saveMutation.mutate();
          }}
          disabled={saveMutation.isPending || timezone === currentTimezone}
          size="sm"
        >
          {saveMutation.isPending && <Loader2 className="size-4 animate-spin" />}
          {t('orgSettings.save')}
        </Button>
      </div>
    </div>
  );
}
